import React, {useState} from 'react';
import isDate from 'lodash/isDate';
import omit from 'lodash/omit';

import Box from '@mui/material/Box';

import ChoiceField from '../common/ChoiceField';
import Form from '../common/Form';
import SchedulerInput from '../availabilities/SchedulerInput';
import SessionFormV1 from './SessionForm';

import useUser from '../../hooks/useUser';
import {
  useCreateSessionMutation,
  useUpdateSessionMutation
} from '../../redux/services/sessionApi';
import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import {getServiceCategories} from '../../helpers/selectOptions';


const sections = ({patientId = 0, sessionTypeId = 0, isEval = false}) => ([
  {
    name: 'patient',
    label: null,
  },
  {
    name: 'general',
    label: '⚙️ General',
    prerequisites: ['patient'],
    componentKey: `general-${patientId}`,
  },
  {
    name: 'scheduling',
    label: '🗓️ Scheduling',
    prerequisites: ['patient', 'general'],
    componentKey: `scheduling-${patientId}-${sessionTypeId}-${isEval}`,
  },
  {
    name: 'options',
    label: '⚙️ More Options',
    prerequisites: ['patient', 'general', 'scheduling'],
    componentKey: `options-${patientId}-${sessionTypeId}-${isEval}`,
  },
]);

const getFields = ({
  sessionTypes = [], 
  providerId,
  patientId, 
  setPatientId,
  serviceCategory = 'PHYSICAL_THERAPY', // FIXME
}) => [
  {
    name: 'patient_id',
    section: 'patient',
    label: 'Patient',
    type: 'selectPatient',
    required: true,
    maxWidth: '250px',
    color: 'default',
    grid: {xs: 12, md: 6},
    notEditable: true,
    onChange: (e, value) => {
      setPatientId(value);
    },
  },
  {
    name: 'session_type_id',
    section: 'general',
    label: 'Session Type',
    type: 'select',
    required: true,
    options: sessionTypes.map(st => ({
      value: st.id, 
      label: getServiceCategories()
        .find(opt => opt.value === st.service_category)
        ?.label
    })),
    maxWidth: '250px',
    grid: {xs: 12, md: 4},
    // parser: (e, value) => ({
    //   service_category: value,
    // }),
  },
  {
    name: 'is_eval',
    section: 'general',
    label: 'Visit Type',
    type: 'choice',
    defaultValue: false,
    required: false,
    maxWidth: '250px',
    grid: {xs: 12, md: 6},
    notEditable: true,
    options: [
      {value: true, label: 'Initial Eval'},
      {value: false, label: 'Followup'},
    ],
  },
  // {
  //   name: 'location_type',
  //   section: 'general',
  //   label: 'Location Type',
  //   type: 'choice',
  //   required: true,
  //   // defaultValue: 'PARTNER',
  //   options: [
  //     {label: '🏋️ Facility', value: 'PARTNER'},
  //     {label: '🏠 In-Home', value: 'HOME'},
  //     {label: '🚗 Mobile', value: 'MOBILE'},
  //     {label: '💻 Virtual', value: 'VIRTUAL'},
  //   ],
  //   maxWidth: '450px',
  //   grid: {xs: 12, md: 8},
  // },
  {
    name: 'scheduling_info',
    section: 'scheduling',
    label: 'Scheduling',
    type: 'scheduling',
    required: true,
    maxWidth: '250px',
    grid: {xs: 12, md: 12},
    color: 'purple',
    Component: props => (
      <SchedulerInput 
        {...props} 
        providerId={providerId} 
        serviceCategory={serviceCategory}
      />
    ),
  },
  {
    name: 'instructions',
    section: 'options',
    label: 'Session Instructions',
    type: 'textarea',
    rows: 2,
    placeholder: 'Add special instructions, prep notes, or equipment needs...',
    // defaultValue: '',
    required: false,
    appendDivider: false,
    // validate: str => !!str,
    grid: {xs: 12, md: 12},
  },
  {
    label: 'Case',
    section: 'options',
    name: 'case_id',
    type: 'selectCase',
    required: true,
    helperText: null,
    placeholder: null,
    patientId,
    maxWidth: '250px',
    grid: {
      xs: 12,
      md: 6,
    },
    validate: () => true,
    parser: (e, value) => ({
      case_id: value?.id,
    }),
  },
  {
    name: 'is_physical_therapy',
    section: 'options',
    label: 'Category',
    type: 'choice',
    defaultValue: true,
    required: false,
    maxWidth: '250px',
    grid: {xs: 12, md: 6},
    options: [
      {value: true, label: 'Restore'},
      {value: false, label: 'Optimize'},
    ],
  },
  // {
  //   label: 'Duration',
  //   section: 'options',
  //   name: 'duration',
  //   type: 'number',
  //   required: true,
  //   defaultValue: 60,
  //   maxWidth: '100px',
  //   min: 0,
  //   max: 1000,
  //   grid: {xs: 12, md: 6},
  // },
  {
    name: 'send_patient_email',
    section: 'options',
    label: 'Send Patient Email',
    type: 'choice',
    defaultValue: true,
    options: [
      {value: true, label: 'Yes'},
      {value: false, label: 'No'},
    ],
    required: false,
    notEditable: true,
    grid: {xs: 12, md: 6},
  },
  {
    name: 'carryforward_only_my_own_notes',
    section: 'options',
    label: 'Carryforward Notes',
    type: 'choice',
    defaultValue: false,
    options: [
      {value: false, label: 'Any Notes'},
      {value: true, label: 'Only My Own Notes'},
    ],
    required: false,
    notEditable: true,
    grid: {xs: 12, md: 6},
  },
  {
    name: 'send_provider_email',
    section: 'options',
    label: 'Send Provider Email',
    type: 'choice',
    defaultValue: true,
    options: [
      {value: true, label: 'Yes'},
      {value: false, label: 'No'},
    ],
    required: false,
    notEditable: true,
    grid: {xs: 12, md: 6},
  },
  // {
  //   name: 'session_meeting_url',
  //   section: 'options',
  //   label: 'Session link',
  //   displayName: 'Session meeting link',
  //   type: 'text',
  //   helperText: 'Add a URL for virtual meetings (starting with https://)',
  //   placeholder: 'https://meet.google.com/abc-defg-hij',
  //   appendDivider: false,
  //   required: false,
  //   hideIfAvailabilityHidden: false,
  //   virtualOnly: true,
  //   validate: str => !!str && /^https:\/\//.test(str),
  //   schema: z.string().nullable().optional(),
  // },
];

const parseLocationDetailsFromSession = session => {
  const locationType = session?.appointment_scheduled?.location_type;
  switch (locationType) {
    case 'PARTNER':
      return {
        location_id: session?.appointment_scheduled?.location?.id,
      };
    case 'VIRTUAL':
      return {
        session_meeting_url: session?.appointment_scheduled?.session_meeting_url,
      };
    case 'HOME':
      return {};
    case 'MOBILE':
      return {
        address: {
          address_line_1: session?.appointment_scheduled?.address_line_1,
          address_line_2: session?.appointment_scheduled?.address_line_2,
          address_city: session?.appointment_scheduled?.address_city,
          address_state: session?.appointment_scheduled?.address_state,
          address_zip: session?.appointment_scheduled?.address_zip,
        },
      };
    default:
      return {};
  }
};

const getDefaultValues = ({
  isEditing, 
  session, 
  sessionTypes,
  sessionType,
  isEval,
}) => ({
  patient_id: isEditing ? session?.patient?.id : null,
  session_type_id: (
    isEditing 
    ? session?.appointment_scheduled?.session_type.id
    : sessionTypes.find(st => st.service_category === 'PHYSICAL_THERAPY')?.id
  ),
  is_eval: isEditing ? !!session?.appointment_scheduled?.is_eval : false,
  duration: isEditing 
    ? session?.duration 
    : (
      sessionType ? (
        isEval
        ? sessionType?.eval_duration_minutes
        : sessionType?.duration_minutes
      )
      : null
    ) || 60,
  case_id: isEditing ? session?.case?.id : null,
  is_physical_therapy: isEditing 
    ? session?.category === 'restore' 
    : sessionType ? sessionType.is_physical_therapy 
    : true,
  send_provider_email: isEditing ? session?.send_provider_email : true,
  send_patient_email: isEditing ? session?.send_patient_email : true,
  carryforward_only_my_own_notes: isEditing ? session?.carryforward_only_my_own_notes : false,
  scheduling_info: isEditing 
    ? { 
      start_time: session.date,
      location_type: session?.appointment_scheduled.location_type,
      duration: session?.duration,
      location_id: session?.appointment_scheduled?.location?.id,
      location_details: parseLocationDetailsFromSession(session) || {},
    } 
    : null,
});

const serialize = data => {
  const sessionData = {
    ...data,
    start_time: (
      isDate(data?.start_time)
      ? data?.start_time.toISOString() 
      : data?.start_time
    ),
    case_id: data?.case_id?.id || data?.case_id,
    // ...omit(data?.scheduling || {}, 'start_time'),
  };
  return sessionData;
};

const handleSubmit = ({
  isEditing, 
  providerId, 
  send, 
  onSuccess, 
  sessionId
} = {}) => async formData => {
  let sessionData = {
    ...omit(formData, 'scheduling_info'),
    start_time: formData?.scheduling_info?.start_time,
    location_type: formData?.scheduling_info?.location_type,
    case_id: formData?.case_id?.id || formData?.case_id,
    duration: formData?.scheduling_info?.duration,
    ...omit(formData?.scheduling_info?.location_details || {}, 'address'),
    ...(formData?.scheduling_info?.address || {}),
    ...(formData?.scheduling_info?.location_id ? {location_id: formData.scheduling_info.location_id} : {}),
  };
  console.log('FORM_DATA', formData, sessionData);
  if (isEditing) {
    sessionData = {
      ...omit(
        sessionData,
        'patient_id', 'start_time', 'is_physical_therapy', 
        'carryforward_only_my_own_notes',
        'send_provider_email', 'send_patient_email'
      ),
    };
    if (formData?.scheduling_info?.start_time) {
      sessionData.date = formData.scheduling_info.start_time;
    }
  }
  const body = (
    isEditing ? {
      id: sessionId,
      body: sessionData,
    }
    : {
      body: {
        ...sessionData,
        provider_id: providerId,
        // session_version: '2.0',
      }
    }
  );
  const request = await send(body);
  if (!request?.error && onSuccess) onSuccess(request);
  // return a failed promise if there was an error
  if (request?.error) return Promise.reject(new Error(request?.error?.data?.errors?.[0]));
  return Promise.resolve(request);
};

const SessionFormV2 = ({
  user,
  session = null,
  sessionTypes = [],
  patient = [],
  priorSession = null,
  isEditing = false,
  backgroundColor = '#ffffff',
  title='',
  onSuccess = e => console.log('onSuccess', e),
}) => {
  const [patientId, setPatientId] = useState(null);
  const [sessionTypeId, setSessionTypeId] = useState(null);
  const [isEval, setIsEval] = useState(false);
  const [post] = useCreateSessionMutation();
  const [put] = useUpdateSessionMutation();
  if (!user?.provider) return null;
  const fields = getFields({
    sessionTypes, 
    patientId, 
    setPatientId,
    providerId: user?.provider?.id,
  }).map(f => 
    isEditing && f?.notEditable
    ? {...f, disabled: true}
    : f
  );
  const send = isEditing ? put : post;
  const onSubmit = handleSubmit({
    isEditing,
    sessionId: session?.id,
    send,
    onSuccess,
    providerId: user.provider.id,
  });
  const defaultValues = getDefaultValues({
    isEditing, 
    session,
    sessionTypes,
    provider: user.provider,
    sessionType: sessionTypes.find(st => st.id === sessionTypeId),
    isEval,
  });
  console.log('defaultValues', defaultValues, session);
  return (
    <Form
      key={session?.id || `new`}
      defaultValues={defaultValues}
      sections={sections({patientId, sessionTypeId, isEval}).map(
        s => ({
          ...s, 
          key: (
            ['patient', 'general'].includes(s.name)
            ? `${s.name}`
            : `${s.name}-${patientId}-${sessionTypeId}-${isEval}`
          )
        })
      )}
      fields={
        fields.map(f => f.name === 'session_type_id' 
          ? {
            ...f,
            onChange: (e, value) => {
              setSessionTypeId(value);
            }
          }
          : f
        ).map(f => f.name === 'scheduling_info' 
          ? {...f, disableLocationChoices: isEditing} 
          : f
        ).map(
          f => ['patient', 'general'].includes(f.section)
          ? {...f, key: f.name}
          : {...f, key: `${f.name}-${patientId}-${sessionTypeId}-${isEval}`},
        )
      }
      backgroundColor={backgroundColor}
      serialize={serialize}
      onSubmit={onSubmit}
      title={title}
    />
  );
};

const SessionFormV2Container = props => {
  const user = useUser();
  const sessionTypesQuery = useGetSessionTypesQuery(
    {provider_id: user.provider.id, session_type_version: '2.0'}
  );
  const childProps = {
    ...props,
    user,
    loading: sessionTypesQuery?.isLoading || !user,
    sessionTypes: sessionTypesQuery?.data?.data || sessionTypesQuery?.data || [],
    onSuccess: props?.afterSubmit || props?.onSuccess,
    onSubmitted: props?.afterSubmit || props?.onSuccess,
  };
  if (childProps?.loading) return <></>;
  const useV1Form = props?.isEditing 
    && props?.session?.appointment_scheduled?.version 
    && props.session.appointment_scheduled.version === '1.0';
  return (
    <Box style={{padding: '1em'}}>
      {
        useV1Form 
        ? <SessionFormV1 {...childProps} />
        : <SessionFormV2 {...childProps} />
      }
    </Box>
  );
};

export default SessionFormV2Container;